import { useMemo } from "react";
import DonutLargeOutlinedIcon from '@mui/icons-material/DonutLargeOutlined';
import { BaseWidget } from "./BaseWidget";
import { ColorControl } from "../controls/ColorControl";
import { FirestoreSelectControl } from "../controls/FirestoreSelectControl";
import useSettings from "../../../../hooks/Settings";
import PersonasStatusVisualization from "../../../dataVisualizations/PersonasStatusVisualization";
import ModulePlaceholder from "./_shared/ModulePlaceholder";
import isTemplateMode from "./_shared/isTemplateMode";
import countPersonasByStatus from "../../../../helpers/personas/countPersonasByStatus";
import { recordInLocation } from '../../../../helpers/locations/locationFilter'
import usePersonaLocations from '../../../../hooks/usePersonaLocations';

// AI-CONTEXT: See docs/features/livereport-widgets-housevisits-personas.md
// PersonasStatusWidget — bar chart of personas by status. Mirrors the
// dashboard's PersonaStatusCard minus the deep-link click; supports an
// editor-controlled location filter via the sidebar (instead of the
// in-card filter chip the dashboard uses).

/**
 * Filter op locatie en tel dan per status.
 *
 * De telling zelf staat in `countPersonasByStatus`, gedeeld met de
 * dashboardkaart — deze widget voegt alleen het locatiefilter toe.
 *
 * @param {Array<{Status?: string, LocationID?: string}>} personas
 * @param {string} locationFilter - '' = alle locaties
 * @returns {Array<{name: string, statusId: string, value: {total: number}}>}
 */
const buildStatusData = (personas, locationFilter) => {
    const arr = Array.isArray(personas) ? personas : [];
    const inLocation = locationFilter
        ? arr.filter((p) => recordInLocation(p, locationFilter))
        : arr;

    return countPersonasByStatus(inLocation);
};

/**
 * @extends {BaseWidget}
 */
export class PersonasStatusWidget extends BaseWidget {

    getName() { return 'Status'; }
    getCategory() { return 'medewerkers'; }
    getRequiredModule() { return 'personas'; }
    countsAllPersonas() { return true; }
    getIcon() { return <DonutLargeOutlinedIcon />; }

    getControls() {
        return {
            'locationFilter': new FirestoreSelectControl({
                label: 'Locatie',
                description: 'Filter op locatie. Leeg = alle locaties.',
                firestore: {
                    collection: 'Locations',
                    valueField: 'docid',
                    labelField: 'Title',
                    orderBy: 'Position',
                    includeAll: true,
                },
            }),
            'graphColor': new ColorControl({
                label: 'Staafkleur',
                description: 'Kleur van de staven. Leeg = gebruik de tenant-secundaire kleur.',
                defaultValue: '',
            }),
        };
    }

    render(props) {
        return <Renderer {...props} />;
    }
}

/**
 * @param {object} props
 * @param {string} [props.locationFilter]
 * @param {string} [props.graphColor]
 * @returns {JSX.Element}
 */
const Renderer = ({ locationFilter, graphColor }) => {
    const { personasOn, SecundairyColor } = useSettings();
    const isTemplate = isTemplateMode();
    const personas = usePersonaLocations();

    const data = useMemo(
        () => buildStatusData(personas, locationFilter),
        [personas, locationFilter]
    );

    if (isTemplate) {
        return <ModulePlaceholder mode="template" />;
    }
    if (personasOn !== 'flex') {
        return <ModulePlaceholder mode="disabled" moduleLabel="Medewerkers" />;
    }

    const color = graphColor || SecundairyColor;

    return (
        <div className="personas-widget-chart">
            <PersonasStatusVisualization data={data} color={color} height={'100%'} />
        </div>
    );
};

export { buildStatusData };
